import React from 'react'
import styled from 'styled-components'
import Card from '@jarvis.oddle/core/components/Card'
import Flex from '@jarvis.oddle/core/components/Flex'
import IconPaypal from '@jarvis.oddle/core/components/Icons/IconPaypal'
import IconCash from '@jarvis.oddle/core/components/Icons/IconCash'
import IconCard from '@jarvis.oddle/core/components/Icons/IconCard'
import IconLinePay from '@jarvis.oddle/core/components/Icons/IconLinePay'
import IconBoostPay from '@jarvis.oddle/core/components/Icons/IconBoostPay'
import IconPayLah from '@jarvis.oddle/core/components/Icons/IconPayLah'
import IconFPX from '@jarvis.oddle/core/components/Icons/IconFPX'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import { numberise } from '@jarvis.oddle/core/tools/style'
import {
  borderRadiusSizes,
  colors,
  iconSizesInPixel
} from '@jarvis.oddle/core/theme/default'
import IconApplePayMark from '@jarvis.oddle/core/components/Icons/IconApplePayMark'
import { paymentMethods, paymentTypes } from 'src/utils/payment'
import { darken, isLight, lighten } from 'src/utils/style'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectThemeSettingData } from 'src/redux/customisation'

interface PaymentMethods extends Oms.V4.PaymentMethodData {
  parsedId: string
}

const Container = styled(Card).attrs(() => ({
  borderRadius: borderRadiusSizes.small,
  justifyContent: 'space-between',
  alignItems: 'center'
}))`
  display: block;
  min-height: 60px;
  background-color: ${props => props.color};
  border: 1px solid ${props => props.color} !important;
  padding: 0 16px !important;
`

const IconContainer = styled('div')`
  margin-inline-end: 0.75em;
  position: relative;
  width: 2.25em;
`

const PaymentTypeHeader = styled('div')`
  font-size: 1.2rem;
  font-weight: 400;
`

const PaymentNameContainer = styled('button')`
  border: 1px solid ${props => props.color};
  box-sizing: border-box;
  box-shadow: none;
  filter: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  vertical-align: middle;
  background: none;
  text-transform: none;
  -webkit-box-align: center;
  align-items: center;
  border-radius: 2px;
  cursor: pointer;
  display: grid;
  font-weight: inherit;
  grid-template-columns: auto 1fr;
  height: calc(1.6em + 32px);
  padding: 0 16px;
  position: relative;
  text-align: start;
  transition: background 0.15s ease,border-color 0.15s ease,color 0.15s ease,box-shadow 0.15s ease,border-radius 0.15s ease;
  margin-left: -17.11111px;
  margin-right: -17.11111px;
  width: calc(100% + 34.22222px);
`

const ContainerRadioDot = styled(Flex).attrs(() => ({
  alignItems: 'center'
}))``

const StyledRadio = styled('svg')`
  color: ${props => props.fill};
  height: 1.2em;
  margin: 0;
  margin-inline-end: 0.75rem;
  width: 1.2em;
`

// To make the icon size fit better to the container
const StyledIconLinePay = styled(IconLinePay)`
  width: ${(numberise(iconSizesInPixel.extraLarge) * 85) / 60}px;
`

const StyledIconCash = styled(IconCash)`
  max-width: 2.5em;
  display: block;
`

const getIconForPayment = (payment: PaymentMethods, color: string = colors.text) => {
  switch (payment.type) {
    case paymentTypes.offline:
      return <StyledIconCash  height="1.2em" width="auto" fill={color} />
    case paymentTypes.chinatrust:
    case paymentTypes.ipay88:
    case paymentTypes.paydollar:
      return <IconCard  height="1.2em" width="auto" fill={color} />
    case paymentTypes.stripe:
    case paymentTypes.stripeExpressAccount:
      if (payment.stripeRequestPaymentButtonType === paymentMethods.APPLE_PAY) {
        return <IconApplePayMark  height="1.2em" width="auto" />
      } else if (payment.paymentMethod === paymentMethods.FPX) {
        return <IconFPX  height="1.2em" width="auto" />
      } else {
        return <IconCard  height="1.2em" width="auto" fill={color} />
      }
    case paymentTypes.boost:
      return <IconBoostPay  height="1.2em" width="auto" fill={color} />
    case paymentTypes.linePay:
      return <StyledIconLinePay  height="1.2em" width="auto" fill={color} />
    case paymentTypes.paypal:
    case paymentTypes.paypalExpressCheckout:
      return <IconPaypal  height="1.2em" width="auto" fill={color} />
    case paymentTypes.paylah:
      return (
        <IconPayLah  height="1.2em" width="auto" fill={color} />
      )
    default:
      return <IconCard  height="1.2em" width="auto" fill={color} />
  }
}

const PaymentTypeCard = ({
  payment,
  selected,
  onClick
}: {
  payment: PaymentMethods
  selected: boolean
  onClick: (payment) => void
}): JSX.Element => {
  const customisationState = useAppSelector(selectThemeSettingData)
  const { settings: { colorContainerColor, colorBodyFont } } = customisationState

  const isEnabled = () => {
    // enableOnCheckout is a recently added field to support the card based promotion feature
    // https://oddle-pm.atlassian.net/browse/OMS-3833
    const { enableOnCheckout } = payment || {}

    // before OMS pushes the required changes, the value of enableOnCheckout is always undefined
    // thus we could safely assume to always enable the payment method which enableonCheckout field is undefined
    return typeof enableOnCheckout === 'boolean' ? enableOnCheckout : true
  }

  const onPaymentClick = () => {
    if (isEnabled()) {
      onClick(payment)
    }
  }

  return (
    <Container
      role='radio'
      onClick={onPaymentClick}
      data-testid='paymentTypeCard'
      enabled={isEnabled()}
      selected={selected}
      color={isLight(colorContainerColor) ? darken(colorContainerColor, 10)
        : lighten(colorContainerColor, 15)}
    >
      <React.Fragment>
        <PaymentTypeHeader>
          <PaymentNameContainer
            color={isLight(colorContainerColor) ? darken(colorContainerColor, 10)
              : lighten(colorContainerColor, 15)}
          >
            <ContainerRadioDot>
              <StyledRadio xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill={colorBodyFont} focusable="false" role="presentation">
                <path
                  d="M14.25 8C14.25 11.4518 11.4518 14.25 8 14.25C4.54822 14.25 1.75 11.4518 1.75 8C1.75 4.54822 4.54822 1.75 8 1.75C11.4518 1.75 14.25 4.54822 14.25 8Z"
                  stroke="currentColor" strokeWidth="1.5" fill="none"/>
                {
                  selected && <circle cx="8" cy="8" r="3.5" fill={colorBodyFont}/>
                }
              </StyledRadio>
              <IconContainer>{getIconForPayment(payment, colorBodyFont)}</IconContainer>
            </ContainerRadioDot>
            <H4
              normalised
              textAlign='left'
              color={colorBodyFont}
              mobileFontSize='1.2rem !important'
            >
              {payment.paymentName}
            </H4>
          </PaymentNameContainer>
        </PaymentTypeHeader>
      </React.Fragment>
    </Container>
  )
}

export default PaymentTypeCard
